var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Modal, ModalHeader, ModalBody, ModalFooter, Table } from '@ui/molecules';
import { Pagination } from '@ui/molecules/Table/components/Pagination';
import { Button } from '@ui/atoms/Button/Button';
import FilterPanel from '@components/FilterPanel';
import FilterTypes from '@constants/FilterTypes';
import useInspectionResubmissions from '../hooks/useInspectionResubmissions';
import { exportResubmissions } from '../services/AnalyticReportsService';
function InspectionLinkCell({ item }) {
    return (React.createElement("a", { href: `/inspections/${item.id}#iim-issues`, target: "_blank", rel: "noopener noreferrer" }, item.inspection));
}
const INITIAL_FILTER_VALUES = {
    technician: [],
    building: []
};
const INITIAL_FILTER_ACTIVE = {
    technician: true,
    building: true
};
const PAGE_SIZE = 10;
const COLUMNS = [
    { dataKey: 'inspection', label: 'Inspection', width: '24%', renderer: InspectionLinkCell },
    { dataKey: 'resubmissions', label: '# of Resubmissions', width: '16%' },
    { dataKey: 'totalIssues', label: 'Total Number of Issues', width: '16%' },
    { dataKey: 'issuesResolved', label: 'Issues Resolved', width: '16%' },
    { dataKey: 'tech', label: 'Tech', width: '14%' },
    { dataKey: 'building', label: 'Building', width: '14%' }
];
function AvgResubmissionsModal({ isOpen, onClose, filters }) {
    var _a, _b;
    const [currentPage, setCurrentPage] = useState(1);
    const [isExporting, setIsExporting] = useState(false);
    const [filterValues, setFilterValues] = useState(INITIAL_FILTER_VALUES);
    const [filterActive, setFilterActive] = useState(INITIAL_FILTER_ACTIVE);
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const handleSort = useCallback((dataKey, direction) => {
        setSortBy(direction ? dataKey : null);
        setSortDirection(direction);
        setCurrentPage(1);
    }, []);
    const { data: response, isLoading } = useInspectionResubmissions(filters, isOpen, currentPage, PAGE_SIZE, filterValues.technician, filterValues.building, sortBy, sortDirection !== null && sortDirection !== void 0 ? sortDirection : undefined);
    const totalItems = (_b = (_a = response === null || response === void 0 ? void 0 : response.meta) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const tableData = useMemo(() => {
        if (!(response === null || response === void 0 ? void 0 : response.data))
            return [];
        return response.data.map((record) => ({
            id: record.id,
            inspection: record.inspection,
            resubmissions: record.resubmissions,
            totalIssues: record.totalIssues,
            issuesResolved: record.issuesResolved,
            tech: record.tech || '--',
            building: record.building || '--'
        }));
    }, [response]);
    const handleFilterChange = useCallback((field) => (value) => {
        setFilterValues((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
        setCurrentPage(1);
    }, []);
    const handleFilterToggle = useCallback((field) => () => {
        setFilterActive((prev) => (Object.assign(Object.assign({}, prev), { [field]: !prev[field] })));
    }, []);
    const handleResetFilters = useCallback(() => {
        setFilterValues(INITIAL_FILTER_VALUES);
        setFilterActive(INITIAL_FILTER_ACTIVE);
        setCurrentPage(1);
    }, []);
    const filtersList = useMemo(() => [
        {
            field: 'technician',
            locale: 'technician',
            type: FilterTypes.GenericMultiSelect,
            active: filterActive.technician,
            value: filterValues.technician,
            onChange: handleFilterChange('technician'),
            onToggle: handleFilterToggle('technician'),
            allowLetterAvatar: true
        },
        {
            field: 'building',
            locale: 'building',
            type: FilterTypes.GenericMultiSelect,
            active: filterActive.building,
            value: filterValues.building,
            onChange: handleFilterChange('building'),
            onToggle: handleFilterToggle('building')
        }
    ], [filterValues, filterActive, handleFilterChange, handleFilterToggle]);
    const handleExportCsv = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setIsExporting(true);
        try {
            const exportResponse = yield exportResubmissions(filters, filterValues.technician, filterValues.building);
            toast.success(exportResponse.message);
        }
        catch (_a) {
            toast.error('Failed to export CSV. Please try again.');
        }
        finally {
            setIsExporting(false);
        }
    }), [filters, filterValues.technician, filterValues.building]);
    if (!isOpen)
        return null;
    const renderContent = () => {
        if (isLoading) {
            return (React.createElement("div", { className: "ia-insights-modal__loading" },
                React.createElement("div", { className: "load-spinner load-32" },
                    React.createElement("svg", null,
                        React.createElement("use", { href: "/map.svg#load-spinner" })))));
        }
        if (tableData.length === 0) {
            return (React.createElement("div", { className: "ia-insights-modal__empty" }, "No inspections with resubmissions found for the selected filters."));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Table, { data: tableData, columns: COLUMNS, variant: "detail", size: "full", sortable: true, sortableColumns: ['resubmissions'], onSort: handleSort, sortBy: sortBy, sortDirection: sortDirection, getItemId: (item) => item.id }),
            totalPages > 1 && (React.createElement(Pagination, { currentPage: currentPage, totalPages: totalPages, totalItems: totalItems, pageSize: PAGE_SIZE, onPageChange: setCurrentPage }))));
    };
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, variant: "base", ariaLabel: "Average Number of Resubmissions", className: "ia-insights-modal__dialog" },
        React.createElement(ModalHeader, { title: "Average Number of Resubmissions", onClose: onClose }),
        React.createElement(ModalBody, { className: "ia-insights-modal__body" },
            React.createElement("div", { className: "ia-insights-modal__header-row" },
                React.createElement("span", { className: "ia-insights-modal__section-title" }, "Inspections with Resubmissions")),
            React.createElement(FilterPanel, { filters: filtersList, onResetFilters: handleResetFilters, toggleDisabled: true }),
            renderContent()),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { variant: "default", onClick: onClose }, "Close"),
            React.createElement(Button, { variant: "primary", onClick: handleExportCsv, disabled: isExporting || totalItems === 0 }, isExporting ? 'Exporting...' : 'Export to CSV'))));
}
export default AvgResubmissionsModal;
